import { supabase } from '../lib/supabase'

export const assessmentService = {
  // Get assessments for a course
  async getAssessments(courseId) {
    try {
      const { data, error } = await supabase
        .from('assessments_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save assessment plan with proper handling of existing marks
  async saveAssessmentPlan(courseId, assessments) {
    try {
      console.log('===SAVING ASSESSMENT PLAN===')
      console.log('Course ID:', courseId)
      console.log('New assessments:', assessments)

      // Get existing assessments to preserve marks relationships
      const { data: existingAssessments } = await supabase
        .from('assessments_clomapper')
        .select('*')
        .eq('course_id', courseId)

      console.log('Existing assessments:', existingAssessments)

      // Create a mapping of assessment names/types to existing IDs
      const existingAssessmentMap = {}
      if (existingAssessments) {
        existingAssessments.forEach(assessment => {
          const key = `${assessment.assessment_type}_${assessment.assessment_name}`
          existingAssessmentMap[key] = assessment.id
        })
      }

      console.log('Existing assessment map:', existingAssessmentMap)

      // Prepare new assessments, preserving IDs where possible
      const assessmentData = assessments.map(assessment => {
        const key = `${assessment.type}_${assessment.name}`
        const existingId = existingAssessmentMap[key]

        const assessmentRecord = {
          course_id: courseId,
          assessment_type: assessment.type,
          assessment_name: assessment.name,
          marks: assessment.marks,
          slt_ci: assessment.sltCI,
          clo_mapping: assessment.clo
        }

        // If we have an existing assessment with the same name/type, preserve its ID
        if (existingId) {
          console.log(`Preserving existing assessment ID ${existingId} for ${key}`)
          assessmentRecord.id = existingId
          // Remove from the map so we know it's been handled
          delete existingAssessmentMap[key]
        } else {
          console.log(`Creating new assessment for ${key}`)
        }

        return assessmentRecord
      })

      console.log('Assessment data to save:', assessmentData)

      // Delete assessments that are no longer needed (and their marks)
      const assessmentsToDelete = Object.values(existingAssessmentMap)
      if (assessmentsToDelete.length > 0) {
        console.log('Deleting unused assessments:', assessmentsToDelete)

        // First delete the marks for these assessments
        const { error: deleteMarksError } = await supabase
          .from('student_marks_clomapper')
          .delete()
          .in('assessment_id', assessmentsToDelete)

        if (deleteMarksError) {
          console.warn('Error deleting marks for unused assessments:', deleteMarksError)
        }

        // Then delete the assessments
        const { error: deleteAssessmentsError } = await supabase
          .from('assessments_clomapper')
          .delete()
          .in('id', assessmentsToDelete)

        if (deleteAssessmentsError) {
          console.warn('Error deleting unused assessments:', deleteAssessmentsError)
        }
      }

      // Use upsert to either insert new or update existing assessments
      const { data, error } = await supabase
        .from('assessments_clomapper')
        .upsert(assessmentData, { onConflict: 'id', ignoreDuplicates: false })
        .select()

      if (error) throw error

      console.log('Successfully saved assessments:', data)
      return { data, error: null }
    } catch (error) {
      console.error('Error saving assessment plan:', error)
      return { data: null, error: error.message }
    }
  },

  // Get CLO statements for a course
  async getCLOStatements(courseId) {
    try {
      const { data, error } = await supabase
        .from('clo_statements_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('clo_code', { ascending: true })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save CLO statements
  async saveCLOStatements(courseId, cloStatements) {
    try {
      // First, delete existing CLO statements for this course
      await supabase
        .from('clo_statements_clomapper')
        .delete()
        .eq('course_id', courseId)

      // Insert new CLO statements
      const cloData = Object.entries(cloStatements).map(([clo, statement]) => ({
        course_id: courseId,
        clo_code: clo,
        statement: statement
      }))

      const { data, error } = await supabase
        .from('clo_statements_clomapper')
        .insert(cloData)
        .select()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get PLO statements for a course
  async getPLOStatements(courseId) {
    try {
      const { data, error } = await supabase
        .from('plo_statements_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('plo_code', { ascending: true })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save PLO statements
  async savePLOStatements(courseId, ploStatements) {
    try {
      // First, delete existing PLO statements for this course
      await supabase
        .from('plo_statements_clomapper')
        .delete()
        .eq('course_id', courseId)

      // Insert new PLO statements
      const ploData = Object.entries(ploStatements).map(([plo, statement]) => ({
        course_id: courseId,
        plo_code: plo,
        statement: statement
      }))

      const { data, error } = await supabase
        .from('plo_statements_clomapper')
        .insert(ploData)
        .select()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get CLO-PLO mapping for a course
  async getCLOPLOMapping(courseId) {
    try {
      const { data, error } = await supabase
        .from('clo_plo_mapping_clomapper')
        .select('*')
        .eq('course_id', courseId)

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save CLO-PLO mapping
  async saveCLOPLOMapping(courseId, mapping) {
    try {
      // First, delete existing mapping for this course
      await supabase
        .from('clo_plo_mapping_clomapper')
        .delete()
        .eq('course_id', courseId)

      // Insert new mapping
      const mappingData = []
      Object.entries(mapping).forEach(([clo, plos]) => {
        Object.entries(plos).forEach(([plo, isMapped]) => {
          mappingData.push({
            course_id: courseId,
            clo_code: clo,
            plo_code: plo,
            is_mapped: isMapped
          })
        })
      })

      const { data, error } = await supabase
        .from('clo_plo_mapping_clomapper')
        .insert(mappingData)
        .select()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get MQF references
  async getMQFReferences() {
    try {
      const { data, error } = await supabase
        .from('mqf_references_clomapper')
        .select('*')
        .order('mqf_code', { ascending: true })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get CLO-PLO-MQF mapping for a course
  async getCLOPLOMQFMapping(courseId) {
    try {
      const { data, error } = await supabase
        .from('clo_plo_mqf_mapping_clomapper')
        .select('*')
        .eq('course_id', courseId)

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save CLO-PLO-MQF mapping
  async saveCLOPLOMQFMapping(courseId, mqfMapping) {
    try {
      // First, delete existing MQF mapping for this course
      await supabase
        .from('clo_plo_mqf_mapping_clomapper')
        .delete()
        .eq('course_id', courseId)

      // Insert new MQF mapping
      const mqfMappingData = []
      Object.entries(mqfMapping).forEach(([clo, ploMapping]) => {
        Object.entries(ploMapping).forEach(([plo, mqfCodes]) => {
          Object.entries(mqfCodes).forEach(([mqfCode, isMapped]) => {
            if (isMapped) {
              mqfMappingData.push({
                course_id: courseId,
                clo_code: clo,
                plo_code: plo,
                mqf_code: mqfCode,
                is_mapped: true
              })
            }
          })
        })
      })

      if (mqfMappingData.length > 0) {
        const { data, error } = await supabase
          .from('clo_plo_mqf_mapping_clomapper')
          .insert(mqfMappingData)
          .select()

        if (error) throw error
        return { data, error: null }
      }
      return { data: [], error: null }
    } catch (error) {
      console.error('Error saving CLO-PLO-MQF mapping:', error)
      return { data: null, error: error.message }
    }
  },

  // Get attainment targets for a course - ENHANCED WITH RETRY LOGIC AND DEBUG
  async getAttainmentTargets(courseId) {
    try {
      console.log('🎯 Loading attainment targets for course:', courseId)

      // First attempt with single record
      const { data, error } = await supabase
        .from('attainment_targets_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .maybeSingle()

      console.log('🎯 Raw DB response:', { data, error })

      if (error) {
        if (error.code === 'PGRST116') {
          console.log('🎯 No existing targets found (PGRST116)')
          return { data: null, error: null }
        }
        throw error
      }

      if (data) {
        console.log('🎯 Successfully loaded targets from DB:', data)
        // Ensure clo_targets and plo_targets are objects
        const sanitizedData = {
          ...data,
          clo_targets: data.clo_targets || {},
          plo_targets: data.plo_targets || {}
        }
        return { data: sanitizedData, error: null }
      }

      console.log('🎯 No data returned')
      return { data: null, error: null }
    } catch (error) {
      console.error('🎯 Error loading attainment targets:', error)
      return { data: null, error: error.message }
    }
  },

  // Save attainment targets for a course - ENHANCED WITH IMPROVED ERROR HANDLING
  async saveAttainmentTargets(courseId, targets) {
    try {
      console.log('🎯 SAVING attainment targets for course:', courseId)
      console.log('🎯 Targets to save:', JSON.stringify(targets, null, 2))

      // Validate the targets structure
      if (!targets.clo_targets || !targets.plo_targets) {
        throw new Error('Invalid targets structure: missing clo_targets or plo_targets')
      }

      // Ensure targets are objects (not arrays or primitives)
      if (typeof targets.clo_targets !== 'object' || typeof targets.plo_targets !== 'object') {
        throw new Error('Targets must be objects')
      }

      // Check if targets already exist
      const existingResult = await this.getAttainmentTargets(courseId)
      let result;

      if (existingResult.data) {
        console.log('🎯 Updating existing targets with ID:', existingResult.data.id)
        // Update existing targets
        result = await supabase
          .from('attainment_targets_clomapper')
          .update({
            clo_targets: targets.clo_targets,
            plo_targets: targets.plo_targets,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingResult.data.id) // Use ID for more reliable update
          .select()
          .single()
      } else {
        console.log('🎯 Creating new targets record')
        // Create new targets
        result = await supabase
          .from('attainment_targets_clomapper')
          .insert({
            course_id: courseId,
            clo_targets: targets.clo_targets,
            plo_targets: targets.plo_targets
          })
          .select()
          .single()
      }

      const { data, error } = result
      if (error) {
        console.error('🎯 Database error during save:', error)
        throw error
      }

      console.log('🎯 Successfully saved targets:', data)

      // Verify the save by reading back
      const verifyResult = await this.getAttainmentTargets(courseId)
      if (verifyResult.data) {
        console.log('🎯 Verification successful - targets persisted:', verifyResult.data)
      } else {
        console.warn('🎯 Verification failed - targets may not be persisted')
      }

      return { data, error: null }
    } catch (error) {
      console.error('🎯 Error saving attainment targets:', error)
      return { data: null, error: error.message }
    }
  },

  // Get SLT configuration for a course - NEW
  async getSLTConfiguration(courseId) {
    try {
      const { data, error } = await supabase
        .from('slt_configuration_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .single()

      if (error && error.code !== 'PGRST116') throw error // PGRST116 is "no rows returned" error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save SLT configuration for a course - NEW
  async saveSLTConfiguration(courseId, courseSLTConfig, teachingSLTByCLO, breakdownConfig) {
    try {
      // Check if configuration already exists
      const { data: existing } = await this.getSLTConfiguration(courseId)
      let result;

      if (existing) {
        // Update existing configuration
        result = await supabase
          .from('slt_configuration_clomapper')
          .update({
            course_slt_config: courseSLTConfig,
            teaching_slt_by_clo: teachingSLTByCLO,
            breakdown_config: breakdownConfig,
            updated_at: new Date().toISOString()
          })
          .eq('course_id', courseId)
          .select()
      } else {
        // Create new configuration
        result = await supabase
          .from('slt_configuration_clomapper')
          .insert({
            course_id: courseId,
            course_slt_config: courseSLTConfig,
            teaching_slt_by_clo: teachingSLTByCLO,
            breakdown_config: breakdownConfig
          })
          .select()
      }

      const { data, error } = result
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Clean up orphaned marks (marks that reference deleted assessments)
  async cleanupOrphanedMarks(courseId) {
    try {
      console.log('Cleaning up orphaned marks for course:', courseId)

      // Get all valid assessment IDs for this course
      const { data: validAssessments } = await supabase
        .from('assessments_clomapper')
        .select('id')
        .eq('course_id', courseId)

      const validAssessmentIds = validAssessments?.map(a => a.id) || []
      console.log('Valid assessment IDs:', validAssessmentIds)

      if (validAssessmentIds.length === 0) {
        // No assessments, delete all marks for this course
        const { error } = await supabase
          .from('student_marks_clomapper')
          .delete()
          .in(
            'student_id',
            supabase
              .from('students_clomapper')
              .select('id')
              .eq('course_id', courseId)
          )

        if (error) console.warn('Error cleaning up all marks:', error)
        return { data: null, error: null }
      }

      // Delete marks that reference non-existent assessments
      const { data, error } = await supabase
        .from('student_marks_clomapper')
        .delete()
        .not('assessment_id', 'in', `(${validAssessmentIds.map(id => `'${id}'`).join(',')})`)
        .in(
          'student_id',
          supabase
            .from('students_clomapper')
            .select('id')
            .eq('course_id', courseId)
        )

      if (error) {
        console.warn('Error cleaning up orphaned marks:', error)
      } else {
        console.log('Cleaned up orphaned marks')
      }

      return { data, error: null }
    } catch (error) {
      console.error('Error in cleanup:', error)
      return { data: null, error: error.message }
    }
  },

  // Template management functions for PLO/CLO (fixes issue #5)
  
  // Get PLO templates
  async getPLOTemplates() {
    try {
      const { data, error } = await supabase
        .from('plo_templates_clomapper')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save PLO template
  async savePLOTemplate(templateName, ploStatements) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user.user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('plo_templates_clomapper')
        .insert({
          user_id: user.user.id,
          template_name: templateName,
          plo_statements: ploStatements
        })
        .select()
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Get CLO templates
  async getCLOTemplates() {
    try {
      const { data, error } = await supabase
        .from('clo_templates_clomapper')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  },

  // Save CLO template
  async saveCLOTemplate(templateName, cloStatements) {
    try {
      const { data: user } = await supabase.auth.getUser()
      if (!user.user) throw new Error('User not authenticated')

      const { data, error } = await supabase
        .from('clo_templates_clomapper')
        .insert({
          user_id: user.user.id,
          template_name: templateName,
          clo_statements: cloStatements
        })
        .select()
        .single()

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: error.message }
    }
  }
}